import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Divider, Space, Switch, Typography } from 'antd';
import moment from 'moment';

import PageHeader from '../../uiKitComponents/pageHeader';
import Table from '../../uiKitComponents/table';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';

import FlowChart from '../../components/flowChart';

import {
  getModerationEngagement,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const getMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);

const initialState = {
  dates: { from: null, to: null },
  is_not_included: true,
};

const columns = [
  {
    dataIndex: 'type',
    title: 'Type',
  },
  {
    dataIndex: 'type_count',
    title: 'Count',
    sortDirections: ['descend', 'ascend', 'descend'],
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.type_count - b.type_count,
  },
];

const TableComponent = ({
  title = '',
  dataSource = [],
  loading = false,
  className,
}) => (
  <div style={{ flex: 1 }} className={className}>
    <Title level={4}>{title}</Title>
    <Table
      rowKey="type"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={false}
    />
  </div>
);

const ModerationEngagement = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('moderation-engagement', {
    ...initialState,
  });
  const { dates, is_not_included } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, moderationEngagement } = useSelector((store) => ({
    cleanOnUnmount: store.reportPages.cleanOnUnmount,
    moderationEngagement: store.reportPages.payload.moderationEngagement,
  }));

  const dispatch = useDispatch();

  const fetchData = useCallback(() => {
    setLoading(true);

    getModerationEngagement({
      from: dates?.from?.string,
      to: dates?.to?.string,
      is_not_included: is_not_included ? 1 : 0,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('moderation-engagement');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, dates, is_not_included]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (moderationEngagement?.selected_range) {
      const { from, to } = moderationEngagement.selected_range;
      form.setFieldsValue({ range: [getMomentDate(from), getMomentDate(to)] });
    }
  }, [moderationEngagement?.selected_range, form]);

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('moderation-engagement');
      cleanReports()(dispatch);
    }
  });

  const onSubmit = ({ range }) => {
    setState((prevState) => ({
      ...prevState,
      dates:
        range && range.length
          ? {
              from: {
                string: moment(range[0].$d).format('DD-MM-YYYY'),
                locale: range[0],
              },
              to: {
                string: moment(range[1].$d).format('DD-MM-YYYY'),
                locale: range[1],
              },
            }
          : initialState.dates,
    }));
  };

  const onReset = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      dates: initialState.dates,
    }));
  };

  if (error)
    return (
      <PageHeader className="page-main-header" title="Moderation Engagement">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="Moderation Engagement">
        <Card className="header-card">
          <h3>Select Dates</h3>
          <Form
            form={form}
            onFinish={onSubmit}
            onReset={onReset}
            className="d-flex align-items-center justify-content-between w-100"
          >
            <Form.Item name="range">
              <RangePicker
                format="DD/MM/YYYY"
                disabledDate={(date) => date.isAfter(moment())}
              />
            </Form.Item>
            <Space>
              <Form.Item>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Space>
          </Form>
        </Card>
      </PageHeader>
      <div className="toggle-pads-view d-inline-flex align-items-center">
        <span className="mr-2">Remove test/internal results</span>
        <Switch
          size="small"
          defaultChecked={is_not_included}
          onChange={(status) =>
            setState((prevState) => ({
              ...prevState,
              is_not_included: status,
            }))
          }
        />
      </div>
      <div className="page-main-content">
        <div className="d-flex w-100" style={{ gap: 32 }}>
          <div style={{ flex: 1 }}>
            <FlowChart
              title="Lifecycle Flow"
              data={moderationEngagement?.range_lifecycle}
            />
            <TableComponent
              title={'Rates'}
              dataSource={moderationEngagement?.rates}
              loading={loading}
            />
          </div>
          <TableComponent
            title="Revision Counts"
            dataSource={moderationEngagement?.revision_counts || []}
            loading={loading}
          />
        </div>
        <Divider style={{ marginTop: 48 }}>Overall Engagement</Divider>
        <div className="d-flex w-100 mt-4" style={{ gap: 32 }}>
          <TableComponent
            title={'Lifecycle Status Breakdown'}
            dataSource={moderationEngagement?.lifecycle_status_breakdown}
            loading={loading}
          />
          <TableComponent
            title={'Moderation Workload'}
            dataSource={moderationEngagement?.moderation_workload}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default ModerationEngagement;

TableComponent.propTypes = {
  title: PropTypes.string,
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      type_count: PropTypes.number.isRequired,
    }),
  ),
  loading: PropTypes.bool,
  className: PropTypes.string,
};
